import usePageSEO from '../hooks/usePageSEO';
import { ArticleSchema } from '../components/JsonLd';

import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Footer from '../components/Footer';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_64eb10e1-fdde-4977-afc2-4761fbf5691b/artifacts/5pkoywp1_Plouton_Logo%20%281%29.png";

const CodeBlock = ({ children }) => (
  <pre className="bg-slate-900 border border-slate-800 rounded-lg p-4 overflow-x-auto my-4">
    <code className="text-sm text-slate-300 font-mono leading-relaxed">{children}</code>
  </pre>
);

export const GuidePage = () => {
  usePageSEO({
    title: 'Getting Started with AI Finance Automation',
    description: 'Set up Plouton AI in weeks, not months. This guide covers agent configuration, workflow mapping, and going live with AP and close automation.',
    path: '/guides/getting-started'
  });

  return (
    <div className="min-h-screen bg-white pt-[108px]">
      <ArticleSchema
        title="Getting Started with Plouton AI"
        description="A complete guide to setting up Plouton AI for your finance team."
        url="https://plouton.co/guides/getting-started"
        datePublished="2026-04-01"
      />
      {/* Article */}
      <article className="py-12 sm:py-16">
        <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12">
          {/* Back link */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
          >
            <Link
              to="/guides"
              className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-700 transition-colors mb-10"
              data-testid="back-to-guides"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              All guides
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* Title */}
            <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-3">
              Getting Started with Plouton AI
            </h1>
            <div className="text-sm text-slate-400 mb-12">
              March 19, 2026 · Plouton AI Team
            </div>

            {/* Content */}
            <div className="space-y-10 text-slate-600 leading-relaxed">
              {/* Introduction */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Introduction</h2>
                <p>
                  Plouton AI uses browser-based agents to automate your finance operations — no API integrations, no 
                  system replacements. This guide walks you through setting up your first workflow.
                </p>
              </section>

              {/* Prerequisites */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Prerequisites</h2>
                <p className="mb-3">Before you begin, make sure you have:</p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">An active Plouton AI account</li>
                  <li className="list-disc text-slate-500">Access to at least one finance system (e.g., SAP, Oracle, NetSuite)</li>
                  <li className="list-disc text-slate-500">Admin or operator-level credentials for the systems you want to automate</li>
                </ul>
              </section>

              {/* Step 1 */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Step 1: Connect Your Workspace</h2>
                <p>
                  Once you've logged into the Plouton dashboard, navigate to <strong className="text-slate-900">Settings → Workspaces</strong> and create a new 
                  workspace for your organization.
                </p>
                <CodeBlock>{`# Example: verifying your workspace connection
plouton workspace status --name "acme-finance"`}</CodeBlock>
                <p>
                  Your workspace acts as the secure boundary for all agent activity. Every action taken by an agent is logged 
                  and auditable within this scope.
                </p>
              </section>

              {/* Step 2 */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Step 2: Configure Your First Agent</h2>
                <p className="mb-3">
                  Agents are configured through workflow templates. Select from pre-built templates or define custom flows:
                </p>
                <ul className="space-y-2 ml-6 mb-4">
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Accounts Payable</strong> — Invoice matching, approval routing, payment scheduling
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Bank Reconciliation</strong> — Statement ingestion, transaction matching, exception flagging
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Financial Close</strong> — Journal entries, intercompany reconciliation, variance analysis
                  </li>
                </ul>
                <p className="mb-2">Each template includes configurable parameters:</p>
                <CodeBlock>{`{
  "workflow": "accounts-payable",
  "schedule": "daily",
  "approval_threshold": 5000,
  "currency": "USD",
  "notification_channel": "slack"
}`}</CodeBlock>
              </section>

              {/* Step 3 */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Step 3: Run and Monitor</h2>
                <p className="mb-3">After configuration, activate your agent. It will:</p>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">Open a secure browser session to your finance system</li>
                  <li className="list-disc text-slate-500">Navigate the interface exactly as a human operator would</li>
                  <li className="list-disc text-slate-500">Execute the configured workflow steps</li>
                  <li className="list-disc text-slate-500">Log every action with full screenshot audit trail</li>
                </ul>
                <p className="mt-3">
                  Monitor real-time progress from the <strong className="text-slate-900">Activity</strong> tab in your dashboard.
                </p>
              </section>

              {/* Key Concepts */}
              <section>
                <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-5">Key Concepts</h2>

                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Browser Agents vs. API Integrations</h3>
                <p className="mb-3">
                  Traditional automation requires API access, custom connectors, and ongoing maintenance. Plouton's 
                  browser agents interact with your systems through the same UI your team uses — meaning:
                </p>
                <ul className="space-y-2 ml-6 mb-6">
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Zero integration overhead</strong> — works with any web-based system
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">No vendor lock-in</strong> — switch systems without rebuilding automations
                  </li>
                  <li className="list-disc text-slate-500">
                    <strong className="text-slate-700">Full auditability</strong> — every click and keystroke is recorded
                  </li>
                </ul>

                <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">Security Model</h3>
                <p>
                  All agent sessions run in isolated, encrypted environments. Credentials are stored using bank-grade 
                  AES-256 encryption and never leave your workspace boundary.
                </p>
              </section>

              {/* Next Steps */}
              <section>
                <h2 className="font-heading text-xl font-semibold text-slate-900 mb-3">Next Steps</h2>
                <ul className="space-y-2 ml-6">
                  <li className="list-disc text-slate-500">
                    Explore the <Link to="/use-cases/accounts-payable-automation" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Use Cases</Link> section for workflow ideas
                  </li>
                  <li className="list-disc text-slate-500">
                    <Link to="/resources" className="text-blue-600 hover:text-blue-700 underline underline-offset-2">Download our ebook</Link> if you haven't already
                  </li>
                  <li className="list-disc text-slate-500">
                    Reach out to our team via the chat widget for hands-on support
                  </li>
                </ul>
              </section>
            </div>
          </motion.div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default GuidePage;
